import { useMemo } from 'react';
import useStore from '../../store/useStore';
import { STYLE_OPTIONS, type StyleMode, type StyleOption } from '../../types/patterns';
import type { ApiGem } from '../../types/api';

/**
 * Unified style picker: Stein | Rille | Welle.
 *
 * When "Stein" is active, a second row of gem chips expands below.
 * Reads gems directly from the first ring template in the store so it can be
 * used in both the hero section and the configurator panel without props.
 */
export default function StylePicker() {
    const styleMode = useStore((s) => s.styleMode);
    const selectedGemId = useStore((s) => s.selectedGemId);
    const ringTemplates = useStore((s) => s.ringTemplates);
    const { setStyleMode, setSelectedGemId } = useStore.getState().methods;

    const gems = useMemo<ApiGem[]>(() => {
        return ringTemplates[0]?.ringPartials[0]?.gems ?? [];
    }, [ringTemplates]);

    return (
        <div className="flex flex-col items-center gap-3">
            {/* Primary mode row */}
            <div className="flex gap-2 flex-wrap justify-center">
                {STYLE_OPTIONS.map((opt) => (
                    <StyleChip
                        key={opt.id}
                        option={opt}
                        isSelected={styleMode === opt.id}
                        onSelect={setStyleMode}
                    />
                ))}
            </div>

            {/* Gem sub-row — only when Stein is active */}
            {styleMode === 'gems' && gems.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 pt-1">
                    {gems.map((gem) => (
                        <GemChip
                            key={gem.id}
                            id={gem.id}
                            label={`${gem.cut.nameDe} · ${gem.diameter}mm`}
                            isSelected={selectedGemId === gem.id}
                            onSelect={setSelectedGemId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Internal chips ─────────────────────────────────────────── */

interface StyleChipProps {
    option: StyleOption;
    isSelected: boolean;
    onSelect: (mode: StyleMode) => void;
}

function StyleChip({ option, isSelected, onSelect }: StyleChipProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect(option.id)}
            title={option.description}
            aria-pressed={isSelected}
            className={[
                'flex items-center gap-1.5 rounded-full px-5 py-2 text-sm tracking-wide',
                'font-medium border transition-all duration-200',
                isSelected
                    ? 'bg-[var(--fg)] text-[var(--bg)] border-[var(--fg)]'
                    : 'bg-transparent border-[var(--fg-muted)] text-[var(--fg-muted)]',
                'hover:border-[var(--fg)] hover:text-[var(--fg)]',
            ].join(' ')}
        >
            <span aria-hidden="true">{option.icon}</span>
            {option.labelDe}
        </button>
    );
}

interface GemChipProps {
    id: string;
    label: string;
    isSelected: boolean;
    onSelect: (id: string | null) => void;
}

function GemChip({ id, label, isSelected, onSelect }: GemChipProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect(id)}
            aria-pressed={isSelected}
            className={[
                'rounded-full px-4 py-1.5 text-xs tracking-wide transition-all duration-200',
                isSelected
                    ? 'bg-[var(--fg)] text-[var(--bg)]'
                    : 'border border-[var(--border)] text-[var(--fg-muted)] hover:border-[var(--fg)]',
            ].join(' ')}
        >
            {label}
        </button>
    );
}
